import { Theme, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { StaffTenantSubscription } from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";

type Row = StaffTenantSubscription;

// Class names the page styles via sx so the account-team columns read as one
// group: a header bar in the staff palette and a faint tint on every cell.
export const ACCOUNT_TEAM_HEADER_CLASS = "account-team-header";
export const ACCOUNT_TEAM_CELL_CLASS = "account-team-cell";

export const accountTeamColumnSx = (theme: Theme) => ({
  [`& .MuiDataGrid-columnHeader.${ACCOUNT_TEAM_HEADER_CLASS}`]: {
    boxShadow: `inset 0 3px 0 ${theme.palette.staff.main}`,
    color: theme.palette.staff.main,
  },
  [`& .MuiDataGrid-cell.${ACCOUNT_TEAM_CELL_CLASS}`]: {
    backgroundColor: alpha(theme.palette.staff.main, 0.08),
  },
});

// Roles that only a few accounts carry stay out of the default view. They are
// still filterable and can be shown from Manage Columns.
export const ACCOUNT_TEAM_HIDDEN_FIELDS = ["dataManagerEmail", "wasteManagerEmail", "pocName"];
export const ACCOUNT_TEAM_HIDDEN_VISIBILITY = Object.fromEntries(
  ACCOUNT_TEAM_HIDDEN_FIELDS.map((field) => [field, false])
);
// Filter-panel order. The Encamp roles are staff-user pickers keyed on email;
// the customer POC is a free-text match on name or email because most POCs
// are not Encamp users.
export const ACCOUNT_TEAM_FILTER_FIELDS = [
  "csmEmail",
  "amEmail",
  "dataManagerEmail",
  "wasteManagerEmail",
  "pocName",
];

type CopyEmail = (email: string) => (event: React.MouseEvent) => void;

type Contact = { name: string | null | undefined; email: string | null | undefined };

export function ContactCell({ name, email, copyEmail }: Contact & { copyEmail: CopyEmail }) {
  if (!name && !email) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  // One line: the name, or the email when the contact matched no Encamp user
  // (and has no SFDC name). The email itself lives in the tooltip and on the
  // clipboard on click; the CSV export carries it as its own column.
  const primary = name ?? email ?? "";
  return (
    <Tooltip title={email ? `Click to copy: ${email}` : ""} placement="top">
      <Typography
        variant="body2"
        fontWeight={name ? 500 : 400}
        noWrap
        onClick={email ? copyEmail(email) : undefined}
        sx={{ minWidth: 0, cursor: email ? "pointer" : "default" }}
      >
        {primary}
      </Typography>
    </Tooltip>
  );
}

const SOURCE_NOTE =
  "From Salesforce (Tenant Ownership Sync report), refreshed daily. Staff names come from the linked Encamp user.";

export function buildAccountTeamColumns(copyEmail: CopyEmail): OmnisearchGridColDef<Row>[] {
  const shared = {
    headerClassName: ACCOUNT_TEAM_HEADER_CLASS,
    cellClassName: ACCOUNT_TEAM_CELL_CLASS,
    flex: 0.8,
    minWidth: 190,
  };
  // Staff roles are keyed on the email because that is what the staff-user
  // picker filters on; the server sorts these keys by name.
  const staffRole = (
    field: string,
    headerName: string,
    role: string,
    contact: (row: Row) => Contact
  ): OmnisearchGridColDef<Row> => ({
    ...shared,
    field,
    headerName,
    description: `${role}. ${SOURCE_NOTE}`,
    filterKeyType: "multipleStaffUser",
    renderCell: ({ row }) => <ContactCell {...contact(row)} copyEmail={copyEmail} />,
  });
  return [
    staffRole("csmEmail", "CSM", "Customer Success Manager", (row) => ({
      name: row.csmName,
      email: row.csmEmail,
    })),
    staffRole("amEmail", "Account Manager", "Account Manager", (row) => ({
      name: row.amName,
      email: row.amEmail,
    })),
    staffRole("dataManagerEmail", "Data Manager", "Data Manager", (row) => ({
      name: row.dataManagerName,
      email: row.dataManagerEmail,
    })),
    staffRole("wasteManagerEmail", "Waste Manager", "Waste Manager", (row) => ({
      name: row.wasteManagerName,
      email: row.wasteManagerEmail,
    })),
    {
      ...shared,
      field: "pocName",
      headerName: "Customer POC",
      description:
        "Customer main point of contact, as recorded in Salesforce. The filter matches name or email.",
      filterKeyType: "text",
      renderCell: ({ row }) => (
        <ContactCell name={row.pocName} email={row.pocEmail} copyEmail={copyEmail} />
      ),
    },
  ];
}
